import { useCallback, useEffect, useState } from "react";
import { UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/chalkApi";
import { ChalkCard, SectionTitle } from "./primitives";
import { StaffMemberRow } from "./StaffMemberRow";
import { InviteStaffSheet } from "./InviteStaffSheet";

export default function TeamStaffCard({ teamId }) {
  const [data, setData] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) return;
    try { setData(await api.listStaff(teamId)); } catch { setData(null); }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  async function toggle(member, key, value, confirmMedical) {
    const prev = member.permissions;
    setData((d) => ({ ...d, staff: d.staff.map((m) => m.user_id === member.user_id ? { ...m, permissions: { ...prev, [key]: value } } : m) }));
    try {
      await api.updateStaffPermissions(teamId, member.user_id, { permissions: { [key]: value }, confirm_medical: confirmMedical });
    } catch (e) {
      toast.error(e.message || "Couldn't save that change.");
      load();
    }
  }

  async function remove(member) {
    try {
      await api.removeStaff(teamId, member.user_id);
      toast.success("Removed from team staff.");
      load();
    } catch (e) { toast.error(e.message || "Couldn't remove."); }
  }

  async function revoke(inv) {
    try { await api.revokeStaffInvite(teamId, inv.id); load(); } catch (e) { toast.error(e.message); }
  }

  const { can_manage: canManage, staff, invites, permission_keys: keys } = data;
  return (
    <div style={{ marginBottom: 18 }} data-testid="team-staff-card">
      <SectionTitle label={canManage ? "Team staff" : "Your role on this team"} />
      {canManage && staff.length === 0 && invites.length === 0 && (
        <ChalkCard style={{ marginBottom: 10 }} data-testid="team-staff-empty">
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--chalk-board)" }}>Bring in a Team Mom.</div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.5 }}>Hand off snacks, volunteers and announcements. You decide exactly what they can see and do.</div>
        </ChalkCard>
      )}
      {staff.map((m) => (
        <StaffMemberRow key={m.user_id} member={m} keys={keys} canManage={canManage} onToggle={toggle} onRemove={remove} />
      ))}
      {invites.map((inv) => (
        <ChalkCard key={inv.id} data-testid={`staff-invite-${inv.id}`} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, borderLeft: "2px solid var(--chalk-border)" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)", overflow: "hidden", textOverflow: "ellipsis" }}>{inv.email}</div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>{inv.label} · invite pending</div>
          </div>
          <button type="button" aria-label="Cancel invite" onClick={() => revoke(inv)} data-testid={`staff-invite-revoke-${inv.id}`}
            style={{ width: 34, height: 34, borderRadius: 8, border: "0.5px solid var(--chalk-border)", background: "#fff", color: "var(--chalk-muted)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <X size={15} aria-hidden="true" />
          </button>
        </ChalkCard>
      ))}
      {canManage && (
        <button type="button" onClick={() => setInviteOpen(true)} data-testid="team-staff-invite-btn"
          style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 14px", borderRadius: 10, border: "1px dashed var(--green-mid)", background: "rgba(46,125,82,0.06)", color: "var(--green-mid)", fontWeight: 700, fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer" }}>
          <UserPlus size={16} aria-hidden="true" /> Invite a Team Mom or staff
        </button>
      )}
      <InviteStaffSheet isOpen={inviteOpen} teamId={teamId} onDismiss={() => setInviteOpen(false)} onInvited={load} />
    </div>
  );
}
